import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatMoney } from '@atek/estimate-pricing';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ApiError } from '@/lib/api';
import { AccountFilterCombobox } from '@/features/quotes/components/AccountFilterCombobox';
import { useConvertToQuote } from '../../hooks/useConvertToQuote';
import { errorMessage } from '../../lib/errors';
import { uiLang } from '../../lib/format';
import type { Estimate } from '../../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  estimate: Estimate;
  /** The estimate's account, else its company's; null makes the user pick one (quotes.account_id is NOT NULL). */
  accountId: string | null;
}

/** Spec § Quote handoff: one budget → POST /v4/quotes → link. Reopens on the link step; never creates a second quote. */
export function ConvertToQuoteDialog({ open, onOpenChange, estimate, accountId }: Props) {
  const { t, i18n } = useTranslation('crm');
  const navigate = useNavigate();
  const convert = useConvertToQuote(estimate.id);
  const budgets = estimate.result.budgets;
  const [budgetKey, setBudgetKey] = useState(convert.pendingBudgetKey ?? budgets[0]?.key ?? '');
  const [pickedAccount, setPickedAccount] = useState('');
  const account = accountId ?? (pickedAccount || null);
  const working = convert.step === 'working';
  const linking = convert.step === 'link_failed' && convert.pendingQuote !== null;
  const lang = uiLang(i18n.language);

  const submit = async () => {
    const done = await convert.run({ budgetKey: convert.pendingBudgetKey ?? budgetKey, accountId: account });
    if (!done) return;
    toast.success(t('estimates.convert.done', { number: done.quote.quoteNumber ?? done.quote.id }));
    onOpenChange(false);
    navigate(`/erp/quotes?id=${done.quote.id}`);
  };

  const failure = convert.error
    ? convert.error instanceof ApiError && convert.error.status === 403 ? t('estimates.convert.noPermission') : errorMessage(convert.error)
    : null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!working) onOpenChange(next); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('estimates.convert.title')}</DialogTitle>
          <DialogDescription>{t('estimates.convert.description')}</DialogDescription>
        </DialogHeader>
        {linking && convert.pendingQuote ? (
          <Alert>
            <AlertDescription className="space-y-2">
              <p>{t('estimates.convert.linkFailed', { number: convert.pendingQuote.quoteNumber ?? convert.pendingQuote.id })}</p>
              <Link to={`/erp/quotes?id=${convert.pendingQuote.id}`} className="text-primary hover:underline">
                {t('estimates.convert.openQuote', { number: convert.pendingQuote.quoteNumber ?? convert.pendingQuote.id })}
              </Link>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <fieldset className="space-y-2" disabled={working}>
              <legend className="mb-1 text-sm font-medium">{t('estimates.convert.budget')}</legend>
              {budgets.map((b) => (
                <label key={b.key} className="flex items-center gap-2 text-sm">
                  <input type="radio" name="convert-budget" value={b.key} checked={budgetKey === b.key} onChange={() => setBudgetKey(b.key)} />
                  <span>{b.label}</span>
                  <span className="text-muted-foreground">{t('estimates.convert.year1', { amount: formatMoney(b.year1, estimate.currency, lang) })}</span>
                </label>
              ))}
            </fieldset>
            {!accountId && (
              <div className="space-y-1">
                <p className="text-sm font-medium">{t('estimates.convert.account')}</p>
                <AccountFilterCombobox
                  value={pickedAccount || 'all'}
                  placeholder={t('estimates.convert.pickAccount')}
                  disabled={working}
                  onChange={(id) => setPickedAccount(id === 'all' ? '' : id)}
                />
                <p className="text-xs text-muted-foreground">{t('estimates.convert.accountHint')}</p>
              </div>
            )}
            {estimate.inputs.contingencyPct > 0 && <p className="text-xs text-muted-foreground">{t('estimates.convert.contingencyHint')}</p>}
          </div>
        )}
        {failure && <Alert variant="destructive"><AlertDescription>{failure}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="outline" disabled={working} onClick={() => onOpenChange(false)}>{t('estimates.actions.cancel')}</Button>
          <Button disabled={working || !budgetKey || (!linking && !account)} onClick={() => void submit()}>
            {working ? t('estimates.convert.working') : linking ? t('estimates.convert.retryLink') : t('estimates.convert.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
